import { useCallback, useEffect, useState } from 'react';
import {
  PaginationData,
  searchActions,
  defaultPaginationData,
} from 'actions/search';

interface Response {
  hasNextPage: boolean;
  paginationData: PaginationData;
  data: string[];
}

function hasSearchQuery(
  type: string | undefined,
  keyword: string | undefined,
): keyword is string {
  return typeof type === 'string' && typeof keyword === 'string' && keyword.trim() !== '';
}

const PAGE_SIZE: number = 25;

function loadItems(
  keyword: string,
  paginationData: PaginationData,
  type?: string,
  selectedKey?: string,
  category?: string,
  searchCurrentLayer?: (keyword: string) => Promise<string[]>,
): Promise<Response> {
  return new Promise((resolve) => {
    switch (type) {
      case 'network': {
        searchActions()
          .searchNetwork(keyword, paginationData.currentPage + 1, PAGE_SIZE)
          .then((result) => {
            resolve({
              hasNextPage:
                result.pagination.currentPage < result.pagination.totalPages,
              paginationData: result.pagination,
              data: result.results,
            });
          })
          .catch(() => {
            resolve({
              hasNextPage: false,
              paginationData: defaultPaginationData,
              data: [],
            });
          });
        break;
      }
      case 'current-layer': {
        if (searchCurrentLayer) {
          searchCurrentLayer(keyword)
            .then((data) => {
              resolve({
                hasNextPage: false,
                paginationData: {
                  ...defaultPaginationData,
                  totalItems: data.length,
                  currentPage: 0,
                  totalPages: 1,
                },
                data,
              });
            })
            .catch(() => {
              resolve({
                hasNextPage: false,
                paginationData: defaultPaginationData,
                data: [],
              });
            });
        } else {
          resolve({
            hasNextPage: false,
            paginationData: defaultPaginationData,
            data: [],
          });
        }
        break;
      }
      default:
        resolve({
          hasNextPage: false,
          paginationData: defaultPaginationData,
          data: [],
        });
        break;
    }
  });
}

export function useLoadItems(
  type?: string,
  selectedKey?: string,
  keyword?: string,
  category?: string,
  searchCurrentLayer?: (keyword: string) => Promise<string[]>,
) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<string[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const [paginationData, setPaginationData] = useState<PaginationData>(
    defaultPaginationData,
  );

  useEffect(() => {
    if (!hasSearchQuery(type, keyword)) {
      setItems([]);
      setHasNextPage(false);
      setPaginationData(defaultPaginationData);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setItems([]);
    setError(undefined);

    loadItems(
      keyword,
      defaultPaginationData,
      type,
      selectedKey,
      category,
      searchCurrentLayer,
    )
      .then((response) => {
        if (cancelled) {
          return;
        }
        setItems(response.data);
        setHasNextPage(response.hasNextPage);
        setPaginationData(response.paginationData);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Search failed'));
        setItems([]);
        setHasNextPage(false);
        setPaginationData(defaultPaginationData);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [type, selectedKey, keyword, category, searchCurrentLayer]);

  const loadMore = useCallback(async () => {
    if (loading || !hasNextPage || items.length === 0 || !hasSearchQuery(type, keyword)) {
      return;
    }

    setLoading(true);

    try {
      const {
        data,
        paginationData: currentPaginationData,
        hasNextPage: newHasNextPage,
      } = await loadItems(
        keyword,
        paginationData,
        type,
        selectedKey,
        category,
        searchCurrentLayer,
      );
      setItems((current) => [...current, ...data]);
      setHasNextPage(newHasNextPage);
      setPaginationData(currentPaginationData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error('Search failed'));
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasNextPage,
    items.length,
    type,
    keyword,
    selectedKey,
    category,
    searchCurrentLayer,
    paginationData,
  ]);

  return { loading, items, paginationData, hasNextPage, error, loadMore };
}
